/**
 * Saving the board (or the selection) as a picture: the part to draw ({@link exportArea}: the
 * items and frames chosen, the rectangle around them with a margin), drawn by the board view out
 * of sight at full size with its own elements (inside a wrapper put out of sight, so the copy is not), then made a PNG by `html-to-image` (the elements
 * copied with their computed styles into an SVG drawn on a canvas) and downloaded.
 *
 * Pictures of items are `blob:` addresses, which the page's CSP lets no one fetch: they are
 * turned into `data:` addresses first, from the pictures already drawn.
 */
import { toBlob } from "html-to-image";
import type { BoardContent, Rect } from "../../../state/board";
import { downloadBytes } from "../../../shared/download";
import { EXPORT_MARGIN_PX, EXPORT_MAX_PIXELS, EXPORT_MAX_SIDE_PX, EXPORT_PIXEL_RATIO, EXPORT_TITLE_ROOM_PX } from "./constants";

/** What a picture holds: the items and frames to draw, in drawing order, and the rectangle around them. */
export interface ExportArea {
  items: string[];
  frames: string[];
  /** Every id drawn (the arrows between them are drawn too). */
  ids: ReadonlySet<string>;
  /** The rectangle drawn, in board units. */
  box: Rect;
}

/** The area holding `what` of `content` (`null`: none of it is on the board). */
export function exportArea(content: BoardContent, what: { items: readonly string[]; frames: readonly string[] }): ExportArea | null {
  const ids = new Set([...what.items, ...what.frames]);
  const items = content.items.filter((i) => ids.has(i.id)).map((i) => i.id);
  const frames = content.frames.filter((f) => ids.has(f.id)).map((f) => f.id);
  let [left, top, right, bottom] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const id of [...items, ...frames]) {
    const p = content.places.get(id)!;
    // A turned item may reach past its rectangle: count its diagonal.
    const pad = p.rotate ? Math.max(p.w, p.h) / 2 : 0;
    const above = frames.includes(id) ? EXPORT_TITLE_ROOM_PX : 0;
    left = Math.min(left, p.x - pad);
    top = Math.min(top, p.y - pad - above);
    right = Math.max(right, p.x + p.w + pad);
    bottom = Math.max(bottom, p.y + p.h + pad);
  }
  if (!Number.isFinite(left)) return null;
  const box = { x: Math.floor(left - EXPORT_MARGIN_PX), y: Math.floor(top - EXPORT_MARGIN_PX), w: Math.ceil(right - left + EXPORT_MARGIN_PX * 2), h: Math.ceil(bottom - top + EXPORT_MARGIN_PX * 2) };
  return { items, frames, ids: new Set([...items, ...frames]), box };
}

/** Pixels per board unit for a picture `w` × `h` board units: {@link EXPORT_PIXEL_RATIO}, less when it would be too big. */
export function pixelRatioFor(w: number, h: number): number {
  return Math.min(EXPORT_PIXEL_RATIO, EXPORT_MAX_SIDE_PX / Math.max(w, h, 1), Math.sqrt(EXPORT_MAX_PIXELS / Math.max(w * h, 1)));
}

/** Waits until the browser drew what was just put in the page. */
const drawn = () => new Promise<void>((done) => requestAnimationFrame(() => requestAnimationFrame(() => done())));

/** Gives every picture inside `root` a `data:` address, drawn from the picture loaded. */
async function inlinePictures(root: HTMLElement): Promise<void> {
  for (const img of root.querySelectorAll("img")) {
    await img.decode().catch(() => {});
    if (img.src.startsWith("data:") || !img.naturalWidth) continue;
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d")?.drawImage(img, 0, 0);
    img.src = canvas.toDataURL("image/png");
    await img.decode().catch(() => {});
  }
}

/** Makes a PNG of `root` (the board view's out-of-sight drawing of `area`) on `background` and downloads it as `name`. */
export async function savePicture(root: HTMLElement, area: ExportArea, background: string, name: string): Promise<void> {
  await drawn();
  await inlinePictures(root);
  const { w, h } = area.box;
  const options = {
    width: w,
    height: h,
    pixelRatio: pixelRatioFor(w, h),
    backgroundColor: background,
  };
  // The app's fonts come from the web: written into the picture when they can be fetched, else the system's.
  const blob = await toBlob(root, options).catch(() => toBlob(root, { ...options, skipFonts: true }));
  if (!blob) throw new Error("the browser made no picture");
  downloadBytes(name, new Uint8Array(await blob.arrayBuffer()), "image/png");
}
